import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const REASONS = [
  "Forgot to swipe",
  "Work From Home",
  "Client Visit",
  "Sick",
  "Travel",
  "Other",
];

export default function RegularisationModal({ onClose, date }) {
  const [form, setForm]     = useState({ date: date || "", req_in: "", req_out: "", reason: REASONS[0], notes: "" });
  const [file, setFile]     = useState(null);
  const [manager, setManager] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [error, setError]   = useState(null);

  // Load manager name from profile
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    fetch(`${API_BASE}/api/profile/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(json => {
        const d = json?.data ?? json;
        setManager(d?.manager || null);
      })
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const body  = new FormData();
      Object.entries(form).forEach(([k, v]) => body.append(k, v));
      if (file) body.append("attachment", file);

      const res = await fetch(`${API_BASE}/api/attendance/regularise`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body,
      });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      setSubmitted(true);
      setTimeout(() => onClose?.(), 1500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4">
      <div className="w-full max-w-lg rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_40px_#00ff9022]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <div>
            <h2 className="text-sm font-semibold text-white">Request Regularisation</h2>
            {form.date && <p className="text-xs text-[#00ff90]/50 mt-0.5">{new Date(form.date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>}
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/40 hover:text-white hover:border-white/20 transition-colors">
            <X size={14} />
          </button>
        </div>

        {submitted ? (
          <div className="px-6 py-10 text-center">
            <p className="text-3xl mb-3">✓</p>
            <p className="text-[#00ff90] font-medium">Request submitted</p>
            <p className="text-white/40 text-sm mt-1">Your manager will be notified.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">

            {/* Date + Times */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5">Date</label>
                <input type="date" value={form.date} onChange={e => set("date", e.target.value)}
                  className="w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors" />
              </div>
              <div>
                <label className="text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5">Check In</label>
                <input type="time" value={form.req_in} onChange={e => set("req_in", e.target.value)}
                  className="w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors" />
              </div>
              <div>
                <label className="text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5">Check Out</label>
                <input type="time" value={form.req_out} onChange={e => set("req_out", e.target.value)}
                  className="w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors" />
              </div>
            </div>

            {/* Reason */}
            <div>
              <label className="text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5">Reason</label>
              <select value={form.reason} onChange={e => set("reason", e.target.value)}
                className="w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors">
                {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5">Additional Notes</label>
              <textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2}
                placeholder="Optional details…"
                className="w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors resize-none placeholder-white/20" />
            </div>

            {/* Attachment */}
            <div>
              <label className="text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5">Attachment (optional)</label>
              <div className="border border-dashed border-[#00ff90]/20 rounded-lg px-4 py-3 bg-[#00ff90]/5 flex items-center gap-3">
                <input type="file" onChange={e => setFile(e.target.files[0])}
                  className="text-sm text-white/50 w-full" />
              </div>
              {file && <p className="text-xs text-white/30 mt-1 truncate">{file.name}</p>}
            </div>

            {/* Manager */}
            <div className="rounded-lg border border-white/5 bg-white/3 px-4 py-3 flex items-center gap-2">
              <span className="text-xs text-white/30 uppercase tracking-wider">Approver</span>
              <span className="text-sm text-white/60 ml-auto">{manager || "Your line manager"}</span>
            </div>

            {error && <p className="text-red-400 text-sm">⚠ {error}</p>}

            {/* Submit */}
            <div className="flex justify-end pt-1">
              <button type="submit" disabled={submitting}
                className="px-6 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 text-sm font-medium hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors">
                {submitting ? "Submitting…" : "Submit Request"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}